import {
  BUBBLE_Z_INDEX,
  COLLAPSED_BUBBLE_SIZE,
  DEFAULT_BACKGROUND_COLOR,
  DEFAULT_BOX_SHADOW,
  DEFAULT_BUBBLE_BORDER_RADIUS,
  DEFAULT_BUBBLE_HEIGHT,
  DEFAULT_BUBBLE_OFFSET_X,
  DEFAULT_BUBBLE_OFFSET_Y,
  DEFAULT_BUBBLE_WIDTH,
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_TEXT_COLOR,
} from "../constants";
import type { BubbleConfig, WidgetConfig, WidgetMode } from "../types";

export class WidgetRenderer {
  private iframe?: HTMLIFrameElement;
  private container?: HTMLElement;
  private wrapper?: HTMLElement;
  private readonly mode: WidgetMode;
  private config: WidgetConfig;
  private isCollapsed = false;
  private isConfigLoaded = false;

  constructor(mode: WidgetMode, config: WidgetConfig) {
    this.mode = mode;
    this.config = config;
    if (config.theme?.primaryColor || config.theme?.textColor) {
      this.isConfigLoaded = true;
    }
  }

  init(container?: string | HTMLElement): HTMLIFrameElement {
    if (this.mode === "bubble") {
      return this.initBubbleMode(container);
    }
    return this.initFullscreenMode(container);
  }

  private initFullscreenMode(
    container?: string | HTMLElement
  ): HTMLIFrameElement {
    if (typeof container === "string") {
      this.container = document.querySelector(container) as HTMLElement;
      if (!this.container) {
        throw new Error(`Container element not found: ${container}`);
      }
    } else if (container instanceof HTMLElement) {
      this.container = container;
    } else {
      this.container = document.body;
    }

    const rect = this.container.getBoundingClientRect();
    if (rect.height === 0) {
      console.warn(
        "[YampaWidget] Container has 0 height! The iframe will not be visible. " +
        "Make sure your container has an explicit height set (e.g., height: 100vh or height: 500px)"
      );
    }

    this.iframe = document.createElement("iframe");
    this.iframe.setAttribute("data-yampa-widget", "true");
    this.iframe.style.border = "none";
    this.iframe.style.width = "100%";
    this.iframe.style.height = "100%";
    this.iframe.src = this.config.widgetUrl;

    this.iframe.addEventListener("error", (e) => {
      console.error("[YampaWidget] Iframe failed to load:", e);
    });

    this.container.appendChild(this.iframe);

    return this.iframe;
  }

  private initBubbleMode(container?: string | HTMLElement): HTMLIFrameElement {
    this.wrapper = document.createElement("div");
    this.wrapper.setAttribute("data-yampa-widget-wrapper", "true");

    const bubbleConfig = this.config.bubble || this.getDefaultBubbleConfig();
    this.applyBubbleStyles(this.wrapper, bubbleConfig);

    if (typeof container === "string") {
      this.container = document.querySelector(container) as HTMLElement;
      if (!this.container) {
        throw new Error(`Container element not found: ${container}`);
      }
    } else if (container instanceof HTMLElement) {
      this.container = container;
    } else {
      this.container = document.body;
    }

    const collapsedIndicator = this.createCollapsedIndicator();
    this.wrapper.appendChild(collapsedIndicator);

    this.iframe = document.createElement("iframe");
    this.iframe.setAttribute("data-yampa-widget", "true");
    this.iframe.style.border = "none";
    this.iframe.style.width = "100%";
    this.iframe.style.height = "100%";

    const widgetUrl = new URL(this.config.widgetUrl);
    widgetUrl.searchParams.set("mode", "bubble");
    this.iframe.src = widgetUrl.toString();

    this.wrapper.appendChild(this.iframe);
    this.container.appendChild(this.wrapper);

    this.setBubbleCollapsed(true);
    return this.iframe;
  }

  getIframe(): HTMLIFrameElement | undefined {
    return this.iframe;
  }

  getWrapper(): HTMLElement | undefined {
    return this.wrapper;
  }

  setBubbleCollapsed(collapsed: boolean): void {
    if (!this.wrapper || this.mode !== "bubble") {
      return;
    }

    this.isCollapsed = collapsed;

    const indicator = this.wrapper.querySelector(
      "[data-yampa-widget-indicator]"
    ) as HTMLElement;

    if (collapsed) {
      this.applyCollapsedStyles(indicator);
    } else {
      this.applyExpandedStyles(indicator);
    }
  }

  updateBubbleConfig(bubbleConfig: BubbleConfig): void {
    if (this.wrapper && this.mode === "bubble") {
      this.applyBubbleStyles(this.wrapper, bubbleConfig);
    }
  }

  updateConfig(config: Partial<WidgetConfig>): void {
    this.config = { ...this.config, ...config };

    // When theme colors are received, mark config as loaded and show the bubble
    if (config.theme && this.mode === "bubble") {
      this.isConfigLoaded = true;

      if (this.isCollapsed) {
        const indicator = this.wrapper?.querySelector(
          "[data-yampa-widget-indicator]"
        ) as HTMLElement;
        this.applyCollapsedStyles(indicator);
      }
    }
  }

  destroy(): void {
    if (this.iframe?.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
    }

    if (this.wrapper?.parentNode) {
      this.wrapper.parentNode.removeChild(this.wrapper);
    }
  }

  private createCollapsedIndicator(): HTMLElement {
    const indicator = document.createElement("div");
    indicator.setAttribute("data-yampa-widget-indicator", "true");
    indicator.style.display = "flex";
    indicator.style.alignItems = "center";
    indicator.style.justifyContent = "center";
    indicator.style.width = "100%";
    indicator.style.height = "100%";
    indicator.style.cursor = "pointer";

    const collapsedSize =
      this.config.bubble?.collapsedSize ?? COLLAPSED_BUBBLE_SIZE;
    const iconSize = Math.min(28, Math.max(16, Math.round(collapsedSize * 0.4)));

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("width", String(iconSize));
    svg.setAttribute("height", String(iconSize));
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute(
      "d",
      "M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
    );
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", "2");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");

    svg.appendChild(path);
    indicator.appendChild(svg);

    return indicator;
  }

  private getDefaultBubbleConfig(): BubbleConfig {
    return {
      width: DEFAULT_BUBBLE_WIDTH,
      height: DEFAULT_BUBBLE_HEIGHT,
      position: {
        horizontal: "right",
        vertical: "bottom",
        offset: {
          x: DEFAULT_BUBBLE_OFFSET_X,
          y: DEFAULT_BUBBLE_OFFSET_Y,
        },
      },
      borderRadius: DEFAULT_BUBBLE_BORDER_RADIUS,
      boxShadow: DEFAULT_BOX_SHADOW,
    };
  }

  private applyBubbleStyles(wrapper: HTMLElement, config: BubbleConfig): void {
    wrapper.style.position = "fixed";
    wrapper.style.zIndex = String(BUBBLE_Z_INDEX);
    wrapper.style.transition = "all 0.3s ease-in-out, opacity 0.2s ease-in-out";
    wrapper.style.overflow = "hidden";
    wrapper.style.opacity = "0";
    wrapper.style.pointerEvents = "none";

    const width =
      typeof config.width === "number"
        ? `${config.width}px`
        : config.width || "400px";
    const height =
      typeof config.height === "number"
        ? `${config.height}px`
        : config.height || "600px";
    wrapper.style.width = width;
    wrapper.style.height = height;

    const position = config.position || {
      horizontal: "right",
      vertical: "bottom",
      offset: {
        x: DEFAULT_BUBBLE_OFFSET_X,
        y: DEFAULT_BUBBLE_OFFSET_Y,
      },
    };
    wrapper.style[position.horizontal] =
      `${position.offset?.x ?? DEFAULT_BUBBLE_OFFSET_X}px`;
    wrapper.style[position.vertical] =
      `${position.offset?.y ?? DEFAULT_BUBBLE_OFFSET_Y}px`;

    wrapper.style.borderRadius = `${config.borderRadius ?? DEFAULT_BUBBLE_BORDER_RADIUS}px`;
    wrapper.style.boxShadow = config.boxShadow || DEFAULT_BOX_SHADOW;
    wrapper.style.backgroundColor = DEFAULT_BACKGROUND_COLOR;
  }

  private applyCollapsedStyles(indicator: HTMLElement | null): void {
    if (!this.wrapper) {
      return;
    }

    const collapsedSize =
      this.config.bubble?.collapsedSize ?? COLLAPSED_BUBBLE_SIZE;
    this.wrapper.style.width = `${collapsedSize}px`;
    this.wrapper.style.height = `${collapsedSize}px`;
    this.wrapper.style.borderRadius = "50%";
    this.wrapper.style.backgroundColor =
      this.config.theme?.primaryColor || DEFAULT_PRIMARY_COLOR;

    if (this.iframe) {
      this.iframe.style.display = "none";
    }
    if (indicator) {
      indicator.style.display = this.isConfigLoaded ? "flex" : "none";
      indicator.style.color =
        this.config.theme?.textColor || DEFAULT_TEXT_COLOR;
    }

    if (this.wrapper) {
      this.wrapper.style.opacity = this.isConfigLoaded ? "1" : "0";
      this.wrapper.style.pointerEvents = this.isConfigLoaded ? "auto" : "none";
    }
  }

  private applyExpandedStyles(indicator: HTMLElement | null): void {
    if (!this.wrapper) {
      return;
    }

    const bubbleConfig = this.config.bubble || this.getDefaultBubbleConfig();
    const width = this.getBubbleDimension(bubbleConfig.width, "400px");
    const height = this.getBubbleDimension(bubbleConfig.height, "600px");

    this.wrapper.style.width = width;
    this.wrapper.style.height = height;
    this.wrapper.style.borderRadius = `${bubbleConfig.borderRadius || 12}px`;
    this.wrapper.style.backgroundColor = "#ffffff";
    this.wrapper.style.opacity = "1";
    this.wrapper.style.pointerEvents = "auto";

    if (this.iframe) {
      this.iframe.style.display = "block";
    }
    if (indicator) {
      indicator.style.display = "none";
    }
  }

  private getBubbleDimension(
    value: number | string | undefined,
    defaultValue: string
  ): string {
    if (typeof value === "number") {
      return `${value}px`;
    }
    return value || defaultValue;
  }
}
